/**
 * Chapter 11, the year: month by month (21) and the rhythm, posts a week at
 * the hours the audience is there (51).
 */
import type { DateRange, MomentConfidence, Quarter } from "@/types/strategyDocument.type";
import { chapterSpec } from "../deck/deckModel";
import {
  Eyebrow, Foot, Glyph, Headline, Key, Slide, V,
  countryName, dayLong, monthYear, platformName, slideText,
  type TemplateContext,
} from "./kit";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTERS: Quarter[] = ["Q1", "Q2", "Q3", "Q4"];

/** 1 → "1st", 22 → "22nd", 13 → "13th". */
function ordinal(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${suffix}`;
}

const parse = (s?: string | null) => (s ? new Date(`${s.slice(0, 10)}T00:00:00Z`) : null);

/**
 * How a dated item reads inside one month: "22nd", "10 to 12", "from the
 * 8th", "to the 9th", "all month"; null when it misses the month.
 */
function inMonth(range: DateRange | null | undefined, year: number, month: number): string | null {
  const start = parse(range?.start);
  if (!start) return null;
  const end = parse(range?.end) ?? start;
  const first = Date.UTC(year, month, 1);
  const last = Date.UTC(year, month + 1, 0);
  if (end.getTime() < first || start.getTime() > last) return null;
  const startsIn = start.getTime() >= first;
  const endsIn = end.getTime() <= last;
  if (startsIn && endsIn) {
    return start.getTime() === end.getTime() ? ordinal(start.getUTCDate()) : `${start.getUTCDate()} to ${end.getUTCDate()}`;
  }
  if (startsIn) return `from the ${ordinal(start.getUTCDate())}`;
  if (endsIn) return `to the ${ordinal(end.getUTCDate())}`;
  return "all month";
}

interface MonthItem { name: string; when: string; tone: MomentConfidence | "brand" }

function Item({ item }: { item: MonthItem }) {
  return (
    <p className="sv-month-item">
      <i data-tone={item.tone} />
      <span><V as="b">{item.name}</V> · <V>{item.when}</V></span>
    </p>
  );
}

/* ── 21 The year ──────────────────────────────────────────────────────── */

export function YearSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const year = doc.year!;
  const t = slideText(ctx);
  const moments = doc.culture?.cultural_moments ?? [];
  const pillars = doc.content?.pillars ?? [];
  const start = parse(ctx.periodStart) ?? new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1));
  /* The period's twelve months, keyed by calendar month: Nov 2026 … Oct 2027 shows as Jan 2027 … Dec 2026. */
  const yearOf = (m: number) => {
    const offset = (m - start.getUTCMonth() + 12) % 12;
    return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + offset, 1)).getUTCFullYear();
  };
  const itemsFor = (m: number) => {
    const y = yearOf(m);
    const culture: MonthItem[] = [];
    const brand: MonthItem[] = [];
    for (const ym of year.year_moments) {
      if (ym.stance === "skip") continue;
      const cm = ym.culture_moment_id ? moments.find((x) => x.id === ym.culture_moment_id) : undefined;
      const range = ym.date_range ?? cm?.date_range;
      const when = inMonth(range, y, m);
      if (!when) continue;
      if (ym.origin === "culture") culture.push({ name: ym.moment, when, tone: cm?.date_confidence ?? "estimate" });
      else brand.push({ name: ym.moment, when: ym.day_label ?? when, tone: "brand" });
    }
    for (const bm of year.big_moments) {
      const when = inMonth(bm.date_range, y, m);
      if (when) brand.push({ name: bm.name, when, tone: "brand" });
    }
    const day = (it: MonthItem) => parseInt(it.when.replace(/\D+/g, " ").trim().split(" ")[0] || "0", 10);
    brand.sort((a, b) => day(a) - day(b));
    return { culture, brand, y };
  };
  const readAt = moments.map((m) => m.evidence?.read_at).find(Boolean);
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="The year, *month by month.*" />
        <div className="sv-year">
          {[0, 1].map((half) => (
            <div key={half} className="sv-year-half">
              <div className="sv-year-quarters">
                {[0, 1].map((q) => {
                  const quarter = QUARTERS[half * 2 + q];
                  const info = year.quarters.find((x) => x.quarter === quarter);
                  const emphasis = info?.pillar_emphasis[0];
                  const pillar = pillars.find((p) => p.id === emphasis?.pillar_id);
                  const first = (half * 2 + q) * 3;
                  return (
                    <p key={quarter} className="sv-year-quarter">
                      <span className="sv-key">{quarter} · {MONTHS[first]} to {MONTHS[first + 2]}</span>
                      {pillar && emphasis ? <span className="sv-pill-dark"><V>{pillar.name} {emphasis.share_pct}%</V></span> : null}
                      {info?.focus ? <V className="sv-year-focus">{info.focus}</V> : null}
                    </p>
                  );
                })}
              </div>
              <div className="sv-year-months">
                {Array.from({ length: 6 }, (_, k) => half * 6 + k).map((m) => {
                  const { culture, brand, y } = itemsFor(m);
                  return (
                    <div key={m} className="sv-month">
                      <p className="sv-month-head"><b>{MONTHS[m]}</b><V>{y}</V></p>
                      <div className="sv-month-group">
                        <p className="sv-month-key">Culture</p>
                        {culture.length ? culture.map((it) => <Item key={it.name} item={it} />) : <p className="sv-month-none">none</p>}
                      </div>
                      <div className="sv-month-group">
                        <p className="sv-month-key sv-month-key-brand">Brand</p>
                        {brand.length ? brand.map((it) => <Item key={it.name} item={it} />) : <p className="sv-month-none">none</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="sv-year-legend">
            <span className="sv-key">Culture</span>
            <span className="sv-year-legend-item"><i data-tone="fixed" />Fixed</span>
            <span className="sv-year-legend-item"><i data-tone="moon_sighting" />Moon-sighting</span>
            <span className="sv-year-legend-item"><i data-tone="estimate" />Estimate</span>
            <span className="sv-key sv-month-key-brand">Brand</span>
            <span className="sv-year-legend-item"><i data-tone="brand" />The brand’s own dates</span>
            <span className="sv-push" />
            <p className="sv-year-source">
              Hijri dates calculated, within a day of the moon sighting, official notices and the client’s own dates{readAt ? <>, read <V>{dayLong(readAt)}</V></> : null}
              {ctx.periodStart && ctx.periodEnd ? <> · the strategy runs <V>{monthYear(ctx.periodStart)}</V> to <V>{monthYear(ctx.periodEnd)}</V></> : null}
            </p>
          </div>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 51 The rhythm ────────────────────────────────────────────────────── */

const hourLabel = (h: number) => (h === 0 ? "12 am" : h < 12 ? `${h} am` : h === 12 ? "12 pm" : `${h - 12} pm`);

export function RhythmSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const volume = doc.content!.volume!;
  const t = slideText(ctx);
  const total = volume.monthly_total;
  const weekly = Math.round((total * 12) / 52);
  const behaviour = doc.audience?.behaviour;
  const online = behaviour?.online_times ?? [];
  const platform = online[0]?.platform;
  const markets = [...new Set(online.filter((o) => o.market && o.platform === platform).map((o) => o.market!))];
  const rows = (markets.length ? markets : [null]).map((market) => {
    const all = online.filter((o) => o.platform === platform && (o.market ?? null) === market && o.day === "all");
    const hours = Array.from({ length: 24 }, (_, h) => all.find((o) => o.hour === h)?.share ?? 0);
    const max = Math.max(...hours, 0.0001);
    const top = [...hours.keys()].sort((a, b) => hours[b] - hours[a]).slice(0, 3);
    const friday = online.filter((o) => o.platform === platform && (o.market ?? null) === market && o.day === "friday").sort((a, b) => b.share - a.share)[0];
    return { market, hours, max, top, peak: top[0], friday: friday?.hour };
  });
  const reaction = doc.year?.reaction;
  const approver = reaction?.approver_name ? `${reaction.approver_name}${reaction.approver_organisation ? ` at ${reaction.approver_organisation}` : ""}` : null;
  const many = volume.per_platform.length > 1;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={`${weekly} posts a week,\n*at the hours they are there.*`} />
        <div className="sv-rhythm">
          <p className="sv-rhythm-volume"><V>{total}</V> posts made a month, <V>{weekly}</V> a week.{many ? " One post can go to several platforms, so the figures below do not add up." : null}</p>
          <div className="sv-rhythm-platforms">
            {volume.per_platform.slice(0, 4).map((v) => (
              <div key={v.platform} className="sv-rhythm-platform">
                <p className="sv-rhythm-top"><Glyph platform={v.platform} size={16} /><b>{platformName(v.platform)}</b></p>
                <p className="sv-rhythm-figure"><V as="b">{v.posts_per_month}</V><span>a month</span></p>
                {v.note ? <V as="p" className="sv-rhythm-note">{v.note}</V> : null}
              </div>
            ))}
          </div>
          {platform && rows.some((r) => r.hours.some(Boolean)) ? (
            <div className="sv-rhythm-times">
              <Key>When they are online on {platformName(platform)} · we post into the peak</Key>
              {rows.map((r) => (
                <div key={r.market ?? "all"} className="sv-rhythm-market">
                  <div className="sv-rhythm-label">
                    <p className="sv-rhythm-name">{r.market ? countryName(r.market).replace(/^the /, "") : "All markets"}</p>
                    <p className="sv-rhythm-peak">Peak <V>{hourLabel(r.peak)}</V>{r.friday != null ? <> · Friday <V>{hourLabel(r.friday)}</V></> : null}</p>
                  </div>
                  <div className="sv-rhythm-hours">
                    {r.hours.map((s, h) => <i key={h} data-peak={r.top.includes(h) || undefined} style={{ height: Math.max(4, Math.round((s / r.max) * 58)) }} />)}
                  </div>
                </div>
              ))}
              <div className="sv-rhythm-axis">{[0, 6, 12, 18, 23].map((h) => <span key={h} style={{ left: `${(h / 23) * 100}%` }}>{hourLabel(h)}</span>)}</div>
            </div>
          ) : null}
        </div>
        <span className="sv-push" />
        <div className="sv-rhythm-rules">
          <div>
            <Key>A trend is joined when</Key>
            <p>It fits a pillar{reaction?.hours_to_post ? <> and can be posted within <V>{reaction.hours_to_post} hours</V></> : null}. Otherwise it is left alone.</p>
          </div>
          <div>
            <Key>Who approves a reaction</Key>
            {approver ? <p><V>{approver}</V>.</p> : <p className="sv-open">Open: who approves a reaction is not set.</p>}
          </div>
          <div className="sv-rhythm-source">
            <Key>Source</Key>
            <p>
              {platform && behaviour?.online_source?.kind === "read" ? <>Online times: connected <V>{platformName(platform)}</V>{behaviour.online_source.read_at ? <>, read <V>{dayLong(behaviour.online_source.read_at)}</V></> : null}. </> : null}
              Volume: chapter {chapterSpec("content")?.number}. Reaction time and approver: the client.
            </p>
          </div>
        </div>
        <Foot ctx={ctx} extra=" · The rhythm" />
      </div>
    </Slide>
  );
}
